import { useUser } from "@clerk/clerk-expo";
import { cacheKeys } from "@constants/cache-keys";
import { mergeUserInfo } from "@memorang/helpers/user";
import type { UserInfo } from "@memorang/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { identifyUser } from "analytics";
import React, { createContext, useEffect, useMemo } from "react";
import {
	fetchUser,
	updateUser as patchUser,
} from "../../../api/hub/user-service";

type State = {
	isLoading: boolean;
	isSignedIn: boolean;
	userId: string | undefined;
	userPublicMetadataId: number | undefined;
	userLogicalId: string | undefined;
	email: string | undefined;
	username: string | undefined;
	firstName: string | undefined;
	lastName: string | undefined;
	isAdmin: boolean;
	userInfo: UserInfo | undefined;
	isFetchingUser: boolean;
};

type Actions = {
	refetchUser: () => Promise<unknown>;
	updateUser: typeof patchUser;
};

type Props = {
	app: string;
	children: React.ReactNode;
};

const AuthContext = createContext<(State & Actions) | null>(null);

export const AuthProvider = ({ app, children }: Props) => {
	const { user, isSignedIn, isLoaded } = useUser();
	const userId = user?.id;

	const queryClient = useQueryClient();
	const userQueryKey = [cacheKeys.user, userId] as const;

	const fetchUserQuery = useQuery({
		queryKey: userQueryKey,
		queryFn: fetchUser,
		enabled: isSignedIn === true,
	});

	const updateUserMutation = useMutation({
		mutationFn: patchUser,
		onSuccess: (response) => {
			queryClient.setQueryData<UserInfo>(userQueryKey, (previous) =>
				previous ? { ...previous, ...response } : response,
			);
		},
	});

	const userInfo = fetchUserQuery.data;
	const mergedUser = useMemo(
		() => mergeUserInfo(userInfo, user),
		[userInfo, user],
	);

	useEffect(() => {
		if (isSignedIn && mergedUser.userLogicalId && mergedUser.email) {
			identifyUser({
				app,
				userId: mergedUser.userLogicalId,
				email: mergedUser.email,
				username: mergedUser.username || "",
			});
		}
	}, [isSignedIn, app, mergedUser]);

	const value = useMemo(
		() => ({
			isLoading: !isLoaded,
			isSignedIn: Boolean(isSignedIn),
			userId,
			userInfo,
			isFetchingUser: fetchUserQuery.isFetching,
			...mergedUser,
			refetchUser: fetchUserQuery.refetch,
			updateUser: updateUserMutation.mutateAsync,
		}),
		[
			fetchUserQuery.isFetching,
			fetchUserQuery.refetch,
			isLoaded,
			isSignedIn,
			userId,
			userInfo,
			mergedUser,
			updateUserMutation.mutateAsync,
		],
	);

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuthContext = () => {
	const context = React.useContext(AuthContext);
	if (context === null) {
		throw new Error("useAuthContext must be used within a AuthProvider");
	}

	return context;
};
